import type {
  ActualVsBudgetCompanyFixture,
  ActualVsBudgetDataAvailability,
  ActualVsBudgetMetricFixture,
  ActualVsBudgetMetricKey,
} from '@/features/actual-vs-budget/types'
import {
  getClosedFixtureMonths,
  getFixtureVariance,
  getFixtureVariancePercent,
  roundFixtureValue,
  sumCompleteFixtureValues,
} from '@/features/actual-vs-budget/fixture-financials'
import { formatUsdMillions } from '@/features/actual-vs-budget/format'
import {
  buildCompanyPlReview,
  type CompanyPlReview,
} from '@/features/actual-vs-budget/pl-review'

export interface CompanyTrajectoryPoint {
  fiscalMonth: number
  periodEnd: string
  monthLabel: string
  actual: number | null
  budget: number | null
  forecast: number | null
  actualLabel: string | null
  budgetLabel: string | null
  forecastLabel: string | null
  isLatestClosed: boolean
}

export interface CompanyMetricSummary {
  fytdActual: number | null
  fytdBudget: number | null
  fytdVarianceAmount: number | null
  fytdVariancePercent: number | null
  fytdPriorYearActual: number | null
  fullYearForecast: number | null
  fullYearBudget: number | null
  fullYearVarianceAmount: number | null
  fullYearVariancePercent: number | null
}

export interface CompanyMetricReview {
  key: ActualVsBudgetMetricKey
  label: string
  unit: ActualVsBudgetMetricFixture['unit']
  budgetAvailability: ActualVsBudgetDataAvailability
  forecastAvailability: ActualVsBudgetDataAvailability
  summary: CompanyMetricSummary
  trajectory: readonly CompanyTrajectoryPoint[]
}

export interface CompanyReview {
  id: string
  name: string
  fund: string
  currency: ActualVsBudgetCompanyFixture['currency']
  fiscalYearLabel: string
  fiscalYearEndLabel: string
  latestClosedPeriodLabel: string
  periodContext: string
  metrics: Record<ActualVsBudgetMetricKey, CompanyMetricReview>
  plReview: CompanyPlReview
}

type TrajectorySeries = 'actual' | 'budget' | 'forecast'

function labelLastVisiblePoint(
  trajectory: CompanyTrajectoryPoint[],
  series: TrajectorySeries,
  label: string
) {
  for (let index = trajectory.length - 1; index >= 0; index -= 1) {
    const point = trajectory[index]

    if (point && point[series] !== null) {
      point[`${series}Label`] = label
      return
    }
  }
}

function getLastVisibleValue(
  trajectory: readonly CompanyTrajectoryPoint[],
  series: TrajectorySeries
) {
  for (let index = trajectory.length - 1; index >= 0; index -= 1) {
    const value = trajectory[index]?.[series]
    if (value !== null && value !== undefined) return value
  }

  return null
}

function formatPeriod(value: string, format: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat('en-US', {
    ...format,
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`))
}

function formatFiscalYearEnd(
  company: ActualVsBudgetCompanyFixture,
  month: 'long' | 'short'
) {
  const latestClosedDate = new Date(
    `${company.latestClosedPeriodEnd}T00:00:00Z`
  )

  return new Intl.DateTimeFormat('en-US', {
    month,
    day: 'numeric',
    timeZone: 'UTC',
  }).format(
    new Date(
      Date.UTC(
        latestClosedDate.getUTCFullYear(),
        company.fiscalYearEnd.month - 1,
        company.fiscalYearEnd.day
      )
    )
  )
}

function buildMetricReview(
  company: ActualVsBudgetCompanyFixture,
  metric: ActualVsBudgetMetricFixture
): CompanyMetricReview {
  const { closedMonths, latestClosedIndex } = getClosedFixtureMonths(
    company,
    metric
  )
  const fytdActual = sumCompleteFixtureValues(
    closedMonths.map(({ actual }) => actual)
  )
  const fytdPriorYearActual = sumCompleteFixtureValues(
    closedMonths.map(({ priorYearActual }) => priorYearActual)
  )
  const fytdBudget = sumCompleteFixtureValues(
    closedMonths.map(({ budget }) => budget)
  )
  const fullYearForecast =
    metric.fullYearForecast === null
      ? null
      : roundFixtureValue(metric.fullYearForecast, 1)
  const fullYearBudget =
    metric.fullYearBudget === null
      ? null
      : roundFixtureValue(metric.fullYearBudget, 1)
  const hasPhasedForecast = company.forecastAvailability === 'phased'

  const trajectory: CompanyTrajectoryPoint[] = metric.months.map(
    (month, index) => ({
      fiscalMonth: month.fiscalMonth,
      periodEnd: month.periodEnd,
      monthLabel: formatPeriod(month.periodEnd, { month: 'short' }),
      actual: index <= latestClosedIndex ? month.actual : null,
      budget: month.budget,
      forecast:
        hasPhasedForecast && index === latestClosedIndex
          ? month.actual
          : hasPhasedForecast && index > latestClosedIndex
            ? month.forecast
            : null,
      actualLabel: null,
      budgetLabel: null,
      forecastLabel: null,
      isLatestClosed: index === latestClosedIndex,
    })
  )

  labelLastVisiblePoint(
    trajectory,
    'actual',
    `Actual ${formatUsdMillions(getLastVisibleValue(trajectory, 'actual'))}`
  )
  labelLastVisiblePoint(
    trajectory,
    'budget',
    `Budget ${formatUsdMillions(getLastVisibleValue(trajectory, 'budget'))}`
  )
  labelLastVisiblePoint(
    trajectory,
    'forecast',
    `Forecast ${formatUsdMillions(getLastVisibleValue(trajectory, 'forecast'))}`
  )

  return {
    key: metric.key,
    label: metric.label,
    unit: metric.unit,
    budgetAvailability: company.budgetAvailability,
    forecastAvailability: company.forecastAvailability,
    summary: {
      fytdActual,
      fytdBudget,
      fytdVarianceAmount: getFixtureVariance(fytdActual, fytdBudget),
      fytdVariancePercent: getFixtureVariancePercent(fytdActual, fytdBudget),
      fytdPriorYearActual,
      fullYearForecast,
      fullYearBudget,
      fullYearVarianceAmount: getFixtureVariance(
        fullYearForecast,
        fullYearBudget
      ),
      fullYearVariancePercent: getFixtureVariancePercent(
        fullYearForecast,
        fullYearBudget
      ),
    },
    trajectory,
  }
}

function getMetric(
  company: ActualVsBudgetCompanyFixture,
  key: ActualVsBudgetMetricKey
) {
  const metric = company.metrics.find((candidate) => candidate.key === key)

  if (!metric) {
    throw new Error(`Missing ${key} fixture for ${company.id}`)
  }

  return buildMetricReview(company, metric)
}

export function buildCompanyReview(
  company: ActualVsBudgetCompanyFixture
): CompanyReview {
  const latestClosedPeriodLabel = formatPeriod(company.latestClosedPeriodEnd, {
    month: 'long',
    year: 'numeric',
  })

  return {
    id: company.id,
    name: company.name,
    fund: company.fund,
    currency: company.currency,
    fiscalYearLabel: company.fiscalYearLabel,
    fiscalYearEndLabel: formatFiscalYearEnd(company, 'long'),
    latestClosedPeriodLabel,
    periodContext: `FYTD through ${latestClosedPeriodLabel} · FY ends ${formatFiscalYearEnd(company, 'short')}`,
    metrics: {
      revenue: getMetric(company, 'revenue'),
      'adjusted-ebitda': getMetric(company, 'adjusted-ebitda'),
    },
    plReview: buildCompanyPlReview(company),
  }
}

export function findCompanyReview(
  companies: readonly ActualVsBudgetCompanyFixture[],
  companyId: string
) {
  const company = companies.find(({ id }) => id === companyId)
  return company ? buildCompanyReview(company) : undefined
}
